import React, { useState, useCallback, useMemo, useEffect } from 'react';
import kpiCategoryService, { KPICategory } from '../../services/kpiCategoryService';
import './SubmissionFilterPanel.css';

export interface SubmissionFilter {
  locations: string[];
  years: number[];
  months: number[];
  kpiCategories: string[];
  includeAllQualityScores: boolean; // New option to include all submissions regardless of quality score
  intelligentFiltering: boolean; // Toggle for intelligent filtering when running analysis
}

// Base type for submissions - only needs fields used for filtering
interface SubmissionBase {
  locationId?: number;
  year: number;
  month: number;
}

interface SubmissionFilterPanelProps {
  submissions: SubmissionBase[];
  onApplyFilter: (filter: SubmissionFilter) => void;
  onClearFilter: () => void;
  getLocationName: (locationId?: number) => string;
  accessibleLocations?: any[]; // Add accessible locations to show all available locations
}

const SubmissionFilterPanel: React.FC<SubmissionFilterPanelProps> = ({
  submissions,
  onApplyFilter,
  onClearFilter,
  getLocationName,
  accessibleLocations = []
}) => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [selectedKpiCategories, setSelectedKpiCategories] = useState<string[]>([]);
  const [includeAllQualityScores, setIncludeAllQualityScores] = useState<boolean>(true);
  const [intelligentFiltering, setIntelligentFiltering] = useState<boolean>(true);
  
  // KPI Categories state
  const [kpiCategories, setKpiCategories] = useState<KPICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);

  // Fetch KPI categories on component mount
  useEffect(() => {
    const fetchKpiCategories = async () => {
      setLoadingCategories(true);
      try {
        const categories = await kpiCategoryService.getKPICategories();
        setKpiCategories(categories);
      } catch (error) {
        console.error('Error fetching KPI categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchKpiCategories();
  }, []);

  // Extract available options from submissions and accessible locations
  const availableOptions = useMemo(() => {
    const submissionLocations = new Set<string>();
    const years = new Set<number>();
    const months = new Set<number>();

    // Get locations from submissions for years/months data
    submissions.forEach(submission => {
      const locationName = getLocationName(submission.locationId);
      submissionLocations.add(locationName);
      years.add(submission.year);
      months.add(submission.month);
    });

    // Get all accessible locations (not just those with submissions)
    const allAccessibleLocationNames = new Set<string>();
    accessibleLocations.forEach(location => {
      allAccessibleLocationNames.add(location.name);
    });
    
    // Also include locations from submissions (in case there are any edge cases)
    submissionLocations.forEach(locationName => {
      allAccessibleLocationNames.add(locationName);
    });

    return {
      locations: Array.from(allAccessibleLocationNames).sort(),
      years: Array.from(years).sort((a, b) => b - a), // Newest first
      months: Array.from(months).sort((a, b) => a - b)
    };
  }, [submissions, getLocationName, accessibleLocations]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleLocationChange = useCallback((location: string) => {
    setSelectedLocations(prev => 
      prev.includes(location) 
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  }, []);

  const handleYearChange = useCallback((year: number) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  }, []);

  const handleMonthChange = useCallback((month: number) => {
    setSelectedMonths(prev => 
      prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  }, []);

  // Auto-apply filter whenever filter state changes
  const autoApplyFilter = useCallback(() => {
    const filter: SubmissionFilter = {
      locations: selectedLocations,
      years: selectedYears,
      months: selectedMonths,
      kpiCategories: selectedKpiCategories,
      includeAllQualityScores: includeAllQualityScores,
      intelligentFiltering: intelligentFiltering
    };
    onApplyFilter(filter);
  }, [selectedLocations, selectedYears, selectedMonths, selectedKpiCategories, includeAllQualityScores, intelligentFiltering, onApplyFilter]);

  // Auto-apply filter whenever any filter state changes
  useEffect(() => {
    autoApplyFilter();
  }, [autoApplyFilter]);

  const handleKpiCategoryChange = useCallback((categoryName: string) => {
    setSelectedKpiCategories(prev => 
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedLocations([]);
    setSelectedYears([]);
    setSelectedMonths([]);
    setSelectedKpiCategories([]);
    setIncludeAllQualityScores(true); // Reset to default checked state
    setIntelligentFiltering(true); // Reset to default enabled state
    onClearFilter();
  }, [onClearFilter]);

  const hasActiveFilters = selectedLocations.length > 0 || selectedYears.length > 0 || selectedMonths.length > 0 || selectedKpiCategories.length > 0 || includeAllQualityScores || intelligentFiltering;

  return (
    <div className="submission-filter-panel">
      <div className="filter-header">
        <h3>Filter Submissions</h3>
        <div className="filter-actions">
          <button 
            className="filter-btn clear-btn" 
            onClick={handleClearFilter}
            disabled={!hasActiveFilters}
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="filter-content">
        {/* Location Filter */}
        <div className="filter-section">
          <h4>Locations</h4>
          <div className="filter-options">
            {availableOptions.locations.map(location => (
              <label key={location} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedLocations.includes(location)}
                  onChange={() => handleLocationChange(location)}
                />
                <span className="checkmark"></span>
                <span className="label-text">{location}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Year Filter */}
        <div className="filter-section">
          <h4>Years</h4>
          <div className="filter-options">
            {availableOptions.years.map(year => (
              <label key={year} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedYears.includes(year)}
                  onChange={() => handleYearChange(year)}
                />
                <span className="checkmark"></span>
                <span className="label-text">{year}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Month Filter */}
        <div className="filter-section">
          <h4>Months</h4>
          <div className="filter-options">
            {availableOptions.months.map(month => (
              <label key={month} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedMonths.includes(month)}
                  onChange={() => handleMonthChange(month)}
                />
                <span className="checkmark"></span>
                <span className="label-text">{monthNames[month - 1]}</span>
              </label>
            ))}
          </div>
        </div>

        {/* KPI Categories Filter */}
        <div className="filter-section">
          <h4>KPI Categories</h4>
          {loadingCategories ? (
            <div className="loading-text">Loading categories...</div>
          ) : (
            <div className="filter-options">
              {kpiCategories.map(category => (
                <label key={category.id} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedKpiCategories.includes(category.category_name)}
                    onChange={() => handleKpiCategoryChange(category.category_name)}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{category.category_name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Quality Score Filter - Hidden but functionality retained */}
        {/* Quality score filtering is enabled by default via includeAllQualityScores state */}

        {/* Intelligent Filtering Toggle - Hidden but functionality retained */}
        {/* Intelligent filtering is enabled by default via intelligentFiltering state */}
      </div>
    </div>
  );
};

export default SubmissionFilterPanel;
